import Table, { TableProps } from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell, { TableCellProps } from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow, { TableRowProps } from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import _ from "lodash";
import * as React from "react";
import TranslatedMessage from "../../translations/TranslatedMessage";
import CustomHeaderCell from "./CustomHeaderCell";
import CustomTableFooter from "./CustomTableFooter";
import GridActions from "./GridActions";

export interface CustomGridFilter<RowType> {
  label: string;
  filterFunction: (element: RowType) => boolean;
  isActive: boolean;
  onFilterChange: (checked: boolean) => void;
}

export interface ColumnDefinition<RowType> {
  columnKey: string;
  columnLabel: string | React.ReactNode;
  cellValue: (row: RowType) => any;
  orderValue?: (row: RowType) => any;
  cellProps?: (row?: RowType) => Partial<TableCellProps>;
  isSortingDisabled?: boolean;
}

type CustomGridProps<RowType> = {
  rows: RowType[];
  additionalHeaderRows?: RowType[];
  rowKey: (row: RowType) => string;
  onRowClick?: (row: RowType) => void;
  columnDefinitions: Array<ColumnDefinition<RowType>>;
  tableProps?: Partial<TableProps>;
  rowsProps?: (row?: RowType, index?: number, isAdditionalHeaderRow?: boolean) => Partial<TableRowProps>;
  // This function will be called with the actual rows that are displayed
  // after changing the sort. Usefull when rendering custom rows instead of passing them
  // as props
  onChangedSort?: (rows: RowType[]) => void;
  initialSorting?: {
    direction: "asc" | "desc";
    orderBy: string;
  };
  addButtonTooltip: NonNullable<React.ReactNode>;
  onAddClick: (event: any) => void;
  tag?: boolean;
  filters?: CustomGridFilter<RowType>[];
  hideAddButton?: boolean;
  hideSearchBar?: boolean;
  additionalButtons?: "" | JSX.Element;
  rowsPerPage?: number;
};

interface CustomGridState {
  direction?: "asc" | "desc";
  orderBy?: string;
  page: number;
  rowsPerPage: number;
  searchToken: string;
}

export class CustomGrid<RowType> extends React.Component<CustomGridProps<RowType>, CustomGridState> {
  constructor(props: CustomGridProps<RowType>) {
    super(props);
    const { initialSorting } = props;

    const hash = this.getParametersFromQueryString();
    const { page, searchToken } = hash;
    const orderBy = hash.orderBy ?? initialSorting?.orderBy;
    const direction = hash.direction ?? initialSorting?.direction ?? "asc";
    const rowsPerPage = hash.rowsPerPage ?? props.rowsPerPage ?? 10;

    this.state = {
      page: page,
      rowsPerPage: rowsPerPage,
      direction: direction,
      orderBy: orderBy,
      searchToken: searchToken,
    };
  }

  public render() {
    const headerRows = this.renderHeadRows();

    const filteredItems = this.getFilteredRows(this.getSortedRows());
    const pagedItems = this.getPagedRows(filteredItems);
    const bodyRows = this.renderBodyRows(pagedItems);

    return (
      <React.Fragment>
        <GridActions
          additionalButtons={this.props.additionalButtons}
          addButtonTooltip={this.props.addButtonTooltip}
          filters={this.props.filters}
          hideAddButton={this.props.hideAddButton}
          hideSearchBar={this.props.hideSearchBar}
          onAddClick={this.props.onAddClick}
          onSearchTokenChanged={this.handleSearchTokenChanged}
          searchToken={this.state.searchToken}
          tag={this.props.tag}
        />
        <div style={boxContainer}>
          {this.renderHeader(headerRows, bodyRows)}
          {this.renderBody(headerRows, bodyRows)}
        </div>
        {this.renderFooter(filteredItems.length)}
      </React.Fragment>
    );
  }

  public componentDidMount() {
    window.addEventListener("hashchange", this.changeGridParametersFromHash, false);
  }

  public componentWillUnmount() {
    window.removeEventListener("hashchange", this.changeGridParametersFromHash, false);
  }

  private changeGridParametersFromHash = () => {
    const hash = this.getParametersFromQueryString();

    this.setState(hash);
  };

  private getParametersFromQueryString(): CustomGridState {
    let hash: CustomGridState = {
      page: 0,
      rowsPerPage: 10,
      searchToken: "",
    };

    if (window.location.hash.length > 1) {
      hash = JSON.parse(atob(window.location.hash.replace("#", "")));
    }

    return hash;
  }

  private setParametersInQueryString(hash: CustomGridState) {
    window.location.hash = btoa(JSON.stringify(hash));
    // That part worked better in some situations but caused issues in Firefox.
    // var state = window.history.state;
    // state.internal = true;
    // window.history.replaceState(state, "", "#" + btoa(JSON.stringify(hash)));
  }

  private handleSearchTokenChanged = (searchToken: string) => {
    let hash = this.getParametersFromQueryString();
    hash.searchToken = searchToken;
    hash.page = 0;
    this.setParametersInQueryString(hash);
    this.setState({ searchToken: searchToken, page: 0 });
  };

  private renderHeadRows = () => {
    const { columnDefinitions } = this.props;

    return (
      <React.Fragment>
        <TableRow>
          {columnDefinitions.map((columnDefinition: ColumnDefinition<RowType>) => {
            const { columnKey, columnLabel, isSortingDisabled } = columnDefinition;
            const { direction, orderBy } = this.state;

            return (
              <CustomHeaderCell
                key={columnKey}
                columnKey={columnKey}
                direction={direction}
                orderBy={orderBy}
                isSortingDisabled={isSortingDisabled}
                onClickSortHandler={this.onClickSortHandler}
              >
                {columnLabel}
              </CustomHeaderCell>
            );
          })}
        </TableRow>
        {this.props.additionalHeaderRows &&
          this.props.additionalHeaderRows.map((row, index) => this.renderAdditionalHeaderTableRow(row, index))}
      </React.Fragment>
    );
  };

  private renderBodyRows = (rows: RowType[]) => {
    return (
      <React.Fragment>
        {!this.props.children && rows.map((row, index) => this.renderTableRow(row, index))}
        {this.props.children}
      </React.Fragment>
    );
  };

  private renderHeader = (headerRows: JSX.Element, bodyRows: JSX.Element) => {
    return (
      <div style={boxRowHeader}>
        <Table {...this.props.tableProps}>
          <TableHead>{headerRows}</TableHead>
          <TableBody style={{ visibility: "collapse" }}>{bodyRows}</TableBody>
        </Table>
      </div>
    );
  };

  private renderBody = (headerRows: JSX.Element, bodyRows: JSX.Element) => {
    const hasRows = this.props.rows.length > 0;
    const { children, tableProps } = this.props;

    return (
      <div style={boxContent}>
        <Table {...tableProps}>
          <TableHead style={{ visibility: "collapse" }}>{headerRows}</TableHead>
          <TableBody>
            {!hasRows && !children && (
              <TableRow>
                <TableCell style={{ textAlign: "center" }} colSpan={100}>
                  <Typography variant="body2">
                    <TranslatedMessage id="table.noRecords" />
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {bodyRows}
          </TableBody>
        </Table>
      </div>
    );
  };

  private renderFooter = (totalRows: number) => {
    const { page, rowsPerPage } = this.state;

    return (
      <div style={boxFooter}>
        <Table>
          <CustomTableFooter style={boxFooter}>
            <TableRow>
              <TablePagination
                labelRowsPerPage={false}
                count={totalRows}
                page={page}
                rowsPerPage={rowsPerPage}
                onChangePage={this.handleOnChangePage}
                rowsPerPageOptions={[5, 10, 25]}
                onChangeRowsPerPage={this.handleChangeRowsPerPage}
              />
            </TableRow>
          </CustomTableFooter>
        </Table>
      </div>
    );
  };

  private handleOnChangePage = (e: any, page: number) => {
    let hash = this.getParametersFromQueryString();
    hash.page = page;
    this.setParametersInQueryString(hash);
    this.setState({ page });
  };

  private handleChangeRowsPerPage = (event: any) => {
    const rowsPerPage: number = parseInt(event.target.value, 10);

    let hash = this.getParametersFromQueryString();
    hash.rowsPerPage = rowsPerPage;
    this.setParametersInQueryString(hash);

    this.setState({ rowsPerPage: rowsPerPage, page: 0 });
  };

  private getOnClickFunction = (row: RowType) => () => {
    const { onRowClick } = this.props;
    if (onRowClick) {
      onRowClick(row);
    }
  };

  private onClickSortHandler = (columnKey: string) => {
    let newDirection: "asc" | "desc" = "desc";
    const { direction, orderBy } = this.state;

    if (orderBy === columnKey && direction === "desc") {
      newDirection = "asc";
    }

    let hash = this.getParametersFromQueryString();
    hash.page = 0;
    hash.orderBy = columnKey;
    hash.direction = newDirection;
    this.setParametersInQueryString(hash);

    this.setState({ orderBy: columnKey, direction: newDirection, page: 0 }, () => {
      const { onChangedSort } = this.props;

      if (onChangedSort) {
        const filteredRows = this.getFilteredRows(this.getSortedRows());
        onChangedSort(this.getPagedRows(filteredRows));
      }
    });
  };

  private getSortedRows(): RowType[] {
    const { columnDefinitions, rows } = this.props;
    const { direction, orderBy } = this.state;

    if (!orderBy) {
      return rows;
    }

    const columnDefinition = columnDefinitions.find((_) => _.columnKey === orderBy);
    if (!columnDefinition) {
      return rows;
    }

    const { cellValue, orderValue } = columnDefinition;
    const valueOrderGetter = orderValue || cellValue;

    const sortedRows = _.orderBy(
      rows,
      (_: RowType) => {
        if (typeof valueOrderGetter(_) === "string") {
          return valueOrderGetter(_).toLowerCase();
        }

        return valueOrderGetter(_);
      },
      direction
    );

    return sortedRows as RowType[];
  }

  private getFilteredRows(rows: RowType[]): RowType[] {
    const { filters, columnDefinitions } = this.props;
    const { searchToken } = this.state;

    if (!searchToken && !filters) {
      return rows;
    }

    let filteredRows: RowType[] = [...rows];

    if (filters) {
      // Apply switch filters
      for (let filter of filters) {
        if (filter.isActive) {
          filteredRows = _.filter(filteredRows, (row: RowType) => {
            return filter.filterFunction(row);
          });
        }
      }
    }

    if (searchToken) {
      // Filter by search token
      filteredRows = _.filter(filteredRows, (row: RowType) => {
        const rowContent = Object.keys(row)
          .map((key) => {
            const value = row[key as keyof RowType];
            if (typeof value === "object") {
              var columnDefinition = columnDefinitions.find((column) => column.columnKey === key);
              return columnDefinition ? columnDefinition?.cellValue(row) : "";
            }
            return value;
          })
          .join()
          .toLowerCase();

        return rowContent.indexOf(this.state.searchToken.toLowerCase()) >= 0;
      });
    }

    return filteredRows as RowType[];
  }

  private getPagedRows = (rows: RowType[]) => {
    if (rows) {
      const { page, rowsPerPage } = this.state;
      const offset = page * rowsPerPage;
      const pagedRows = rows.slice(offset, offset + rowsPerPage);

      return pagedRows;
    }

    return rows;
  };

  private renderTableRow = (row: RowType, index: number): JSX.Element => {
    const { columnDefinitions, rowKey, onRowClick } = this.props;

    return (
      <TableRow
        key={rowKey(row)}
        hover={true}
        onClick={this.getOnClickFunction(row)}
        style={!!onRowClick ? { cursor: "pointer" } : {}}
        {...(this.props.rowsProps && this.props.rowsProps(row, index))}
      >
        {columnDefinitions.map((columnDefinition) => (
          <TableCell
            key={columnDefinition.columnKey + rowKey(row)}
            {...(columnDefinition.cellProps && columnDefinition.cellProps(row))}
          >
            {columnDefinition.cellValue(row)}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  private renderAdditionalHeaderTableRow = (row: RowType, index: number): JSX.Element => {
    const { columnDefinitions, rowKey, rowsProps } = this.props;

    return (
      <TableRow key={rowKey(row)} {...(rowsProps && rowsProps(row, index, true))}>
        {columnDefinitions.map((columnDefinition) => (
          <TableCell
            key={columnDefinition.columnKey + rowKey(row)}
            {...(columnDefinition.cellProps && columnDefinition.cellProps(row))}
          >
            {columnDefinition.cellValue(row)}
          </TableCell>
        ))}
      </TableRow>
    );
  };
}

const boxContainer: React.CSSProperties = {
  display: "flex",
  flexFlow: "column",
  maxHeight: "calc(100vh - 221px)",
  maxWidth: "100%",
  overflowX: "auto",
};

const boxRowHeader: React.CSSProperties = {
  flex: "0 1 auto",
};

const boxContent: React.CSSProperties = {
  display: "flex",
  flexFlow: "column",
  flex: "1 1 auto",
};

const boxFooter: React.CSSProperties = {
  flex: "0 1 auto",
};
